import { useState } from 'react';
import PropTypes from 'prop-types';
import { Paneset, Loading, Layout } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import ViewReportBuilder from '../../components/reports/builders/shared/ViewReportBuilder';
import ResultsPreview from '../../components/reports/results/preview/ResultsPreview';
import useURLParams from '../../hooks/useURLParams';
import useCubeQuery from '../../hooks/useQuery';

const WIDGET_QUERY_KEY = 'widget_pending_query';

const CATALOGING_SECTIONS = [
  {
    formField: 'instanceFields', filtersField: 'instanceFilters', fieldType: 'dimension',
    label: <FormattedMessage id="ui-reports.catalogingReports.instanceFields.title" defaultMessage="Bibliographic Data (Instances)" />,
    description: <FormattedMessage id="ui-reports.catalogingReports.instanceFields.description" defaultMessage="Select fields and filters for bibliographic data:" />,
    fields: [
      'cataloging_view.title', 'cataloging_view.instance_hrid', 'cataloging_view.contributors',
      'cataloging_view.identifiers', 'cataloging_view.subjects', 'cataloging_view.languages',
      'cataloging_view.publication_info', 'cataloging_view.resource_type', 'cataloging_view.resource_status',
      'cataloging_view.instance_created_date', 'cataloging_view.instance_updated_date', 'cataloging_view.catalogeddate',
    ],
  },
  {
    formField: 'holdingsFields', filtersField: 'holdingsFilters', fieldType: 'dimension',
    label: <FormattedMessage id="ui-reports.catalogingReports.holdingsFields.title" defaultMessage="Holdings Data" />,
    description: <FormattedMessage id="ui-reports.catalogingReports.holdingsFields.description" defaultMessage="Select fields and filters for holdings data:" />,
    fields: [
      'cataloging_view.holdings_hrid', 'cataloging_view.call_number', 'cataloging_view.call_number_type',
      'cataloging_view.location_name', 'cataloging_view.library_name', 'cataloging_view.campus_name',
    ],
  },
  {
    formField: 'itemFields', filtersField: 'itemFilters', fieldType: 'dimension',
    label: <FormattedMessage id="ui-reports.catalogingReports.itemFields.title" defaultMessage="Item Data" />,
    description: <FormattedMessage id="ui-reports.catalogingReports.itemFields.description" defaultMessage="Select fields and filters for item data:" />,
    fields: [
      'cataloging_view.item_hrid', 'cataloging_view.barcode', 'cataloging_view.item_status',
      'cataloging_view.material_type', 'cataloging_view.loan_type', 'cataloging_view.item_location_name',
    ],
  },
  {
    formField: 'measures', filtersField: 'measuresFilters', fieldType: 'measure',
    label: <FormattedMessage id="ui-reports.catalogingReports.measures.title" defaultMessage="Statistics & Measures" />,
    description: <FormattedMessage id="ui-reports.catalogingReports.measures.description" defaultMessage="Select statistical measures and filters:" />,
    fields: [
      'cataloging_view.total_instances', 'cataloging_view.total_holdings', 'cataloging_view.total_items',
      'cataloging_view.instances_created', 'cataloging_view.holdings_created', 'cataloging_view.items_created',
    ],
  },
];

const CIRCULATION_SECTIONS = [
  {
    formField: 'loanFields', filtersField: 'loanFilters', fieldType: 'dimension',
    label: <FormattedMessage id="ui-reports.circulationReports.loanFields.title" defaultMessage="Loan Data" />,
    description: <FormattedMessage id="ui-reports.circulationReports.loanFields.description" defaultMessage="Select fields and filters for loan data:" />,
    fields: [
      'circulation_view.loan_id', 'circulation_view.loan_date', 'circulation_view.due_date',
      'circulation_view.return_date', 'circulation_view.loan_status', 'circulation_view.renewal_count',
    ],
  },
  {
    formField: 'patronFields', filtersField: 'patronFilters', fieldType: 'dimension',
    label: <FormattedMessage id="ui-reports.circulationReports.patronFields.title" defaultMessage="Patron Data" />,
    description: <FormattedMessage id="ui-reports.circulationReports.patronFields.description" defaultMessage="Select fields and filters for patron data:" />,
    fields: [
      'circulation_view.borrower_username', 'circulation_view.borrower_first_name',
      'circulation_view.borrower_last_name', 'circulation_view.patron_group_name',
    ],
  },
  {
    formField: 'itemFields', filtersField: 'itemFilters', fieldType: 'dimension',
    label: <FormattedMessage id="ui-reports.circulationReports.itemFields.title" defaultMessage="Item Data" />,
    description: <FormattedMessage id="ui-reports.circulationReports.itemFields.description" defaultMessage="Select fields and filters for item data:" />,
    fields: [
      'circulation_view.title', 'circulation_view.item_barcode', 'circulation_view.call_number',
      'circulation_view.material_type', 'circulation_view.location_name',
    ],
  },
  {
    formField: 'measures', filtersField: 'measuresFilters', fieldType: 'measure',
    label: <FormattedMessage id="ui-reports.circulationReports.measures.title" defaultMessage="Statistics & Measures" />,
    description: <FormattedMessage id="ui-reports.circulationReports.measures.description" defaultMessage="Select statistical measures and filters:" />,
    fields: [
      'circulation_view.total_loans', 'circulation_view.loans_created',
      'circulation_view.total_renewals', 'circulation_view.total_borrowers',
    ],
  },
];

const BUILDER_CONFIGS = {
  cataloging: {
    viewName: 'cataloging_view',
    sections: CATALOGING_SECTIONS,
    timeDimension: 'cataloging_view.instance_created_date',
    title: <FormattedMessage id="ui-reports.catalogingReports.title" defaultMessage="Cataloging Report Builder" />,
    reportType: 'statistical',
  },
  circulation: {
    viewName: 'circulation_view',
    sections: CIRCULATION_SECTIONS,
    timeDimension: 'circulation_view.loan_date',
    title: <FormattedMessage id="ui-reports.circulationReports.title" defaultMessage="Circulation Report Builder" />,
    reportType: 'statistical',
  },
};

function WidgetBuilderRoute(props) {
  const pathParts = props.location.pathname.split('/');
  // /reports/dashboards/:dashboardId/widgets/create/builder/:builderType
  const dashboardId = pathParts[3];
  const builderType = pathParts[7] || 'cataloging';

  const config = BUILDER_CONFIGS[builderType] || BUILDER_CONFIGS.cataloging;

  const [formState, setFormState] = useURLParams('form_state', '');
  const [showBuilderPane, setShowBuilderPane] = useState(true);
  const [currentQuery, setCurrentQuery] = useState('');
  const [queryOptions, setQueryOptions] = useState({});
  const [displayMethod, setDisplayMethod] = useState('table');
  const [resultSet, error, isLoading] = useCubeQuery(currentQuery, queryOptions);

  const handleClose = () => {
    props.history.push(`/reports/dashboards/${dashboardId}/widgets/create`);
  };

  const handleUseQuery = (query) => {
    sessionStorage.setItem(WIDGET_QUERY_KEY, JSON.stringify({
      query,
      builderType,
      displayMethod,
    }));
    props.history.push(`/reports/dashboards/${dashboardId}/widgets/create`);
  };

  return (
    <Paneset>
      <ViewReportBuilder
        viewName={config.viewName}
        sections={config.sections}
        timeDimension={config.timeDimension}
        builderTitle={config.title}
        showBuilderPane={showBuilderPane}
        setShowBuilderPane={setShowBuilderPane}
        handleClose={handleClose}
        formState={formState}
        setFormState={setFormState}
        setQuery={(query, options = {}) => {
          setCurrentQuery(query);
          setQueryOptions(options);
        }}
        isLoading={isLoading}
      />
      <ResultsPreview
        showQueryBuilderPane={showBuilderPane}
        setShowQueryBuilderPane={setShowBuilderPane}
        showSavePane={false}
        setShowSavePane={() => {}}
        handleClose={handleClose}
        query={currentQuery}
        setQuery={setCurrentQuery}
        resultSet={resultSet}
        error={error}
        isLoading={isLoading}
        reportType={config.reportType}
        displayMethod={displayMethod}
        setDisplayMethod={setDisplayMethod}
        onUseQuery={handleUseQuery}
      />
    </Paneset>
  );
}

WidgetBuilderRoute.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.object,
};

const WIDGET_FORM_STATE_KEY = 'widget_form_state';

export { WIDGET_QUERY_KEY };
export default WidgetBuilderRoute;
